import { Select } from "antd";
import Modal from "../../../commonComponents/Modal/Modal";
import "./Organigrama.scss";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { CaretDownFilled } from "@ant-design/icons";
import { driveApi } from "../../../DriveRequest";
import FlowChart from "./FlowChart";
import { useMediaQuery } from "react-responsive";
import { getObjectChanges, setNestedValue } from "../../../../utils";
import {
  DashboardBricks,
  FilterIconButton,
  HamburgerIcon,
} from "../../../../assets/icons";
import dayjsTZ from "../../../../utils/dayjs";
import { ReactFlowProvider } from "@xyflow/react";
import { sortArrayByIds } from "./utils";
import MondayButton from "../../../commonComponents/MondayButton/MondayButton";
import { employeeHasRole } from "../../../../utils/employeeRoles";
const { Option } = Select;

const TEAM_FIELD_ID = "2e9f7112-1775-4b69-b55e-3134333bfdf4";
const EMPLOYEE_STATUSES_FIELD_ID = "ea2e2537-0b6a-4292-aca8-62eafecbfad1";
const ORGANIGRAMA_ORDER_FIELD_ID = "899555c7-f0f8-4d84-aba5-038c507f0a5e";
const ADMIN_DEP_ID = "77580ca0-d85d-11ec-bd74-ed7d1f113010";

const defaultFilters = {
  departmentRoles: {
    employees: { employeeTeam: "Të gjithë", employeeStatus: "Aktiv" },
  },
};

const Organigrama = ({ open, setOpen }) => {
  const { employees } = useSelector((state) => state.employeesList);
  const { departments } = useSelector((state) => state.departments);
  const { accessToken } = useSelector((state) => state.accessToken);
  const { programFields } = useSelector((state) => state.programFields);
  const { isDarkMode, users } = useSelector((state) => state.usersList);

  const driveRequest = driveApi({ accessToken });

  const [fetchedSrc, setFetchedSrc] = useState({});
  const [siderOpen, setSiderOpen] = useState(true);
  const [activeFilter, setActiveFilter] = useState(defaultFilters);

  const changeFilter = (path, value) => {
    setActiveFilter((prev) => setNestedValue(prev, path, value));
  };

  const { teams, employeesOrder, teamsOptions, employeeStatuses } =
    useMemo(() => {
      let teams = [];
      let teamsOptions = [];
      let employeeStatuses = [];
      let employeesOrder;

      if (!programFields) {
        return { teams, teamsOptions, employeesOrder, employeeStatuses };
      }

      for (const el of programFields) {
        if (el?.fieldId === TEAM_FIELD_ID) {
          teams = el?.fieldOptions || [];
        }
        if (el?.fieldId === EMPLOYEE_STATUSES_FIELD_ID) {
          employeeStatuses = (el?.fieldOptions || []).filter(
            (item) => item?.name !== "Logs"
          );
        }
        if (el?.fieldId === ORGANIGRAMA_ORDER_FIELD_ID) {
          employeesOrder = el?.fieldOptions;
        }
      }

      teamsOptions = teams.concat(["Të gjithë"]).map((el) => (
        <Option key={el} value={el}>
          {el}
        </Option>
      ));

      return { teams, teamsOptions, employeeStatuses, employeesOrder };
    }, [programFields]);

  const mergedDepartments = useMemo(() => {
    if (!departments || !employees) return null;
    let missingDeps = {};
    const employeesByDep = employees.reduce((acc, emp) => {
      let depId = emp.employeeDepartmentId;
      if (!depId) {
        const depName = emp.employeeDepartmentName;
        depId = missingDeps[depName];

        if (!missingDeps[depName]) {
          const idx = departments.findIndex(
            (el) => el?.departmentName === depName
          );
          if (idx !== -1) {
            missingDeps[depName] = departments[idx].departmentId;
            depId = missingDeps[depName];
          }
        }
      }

      if (!acc[depId]) {
        acc[depId] = [];
      }
      acc[depId].push(emp);
      return acc;
    }, {});

    const deps = departments.map((el) => {
      const usersItems = users?.allUsers?.Items || [];
      const idx = usersItems.findIndex(
        (e) => e?.identityId === el?.departmentManagerId
      );
      const supervisorUser = idx !== -1 ? usersItems[idx] : undefined;
      // No match is normal: users may still be loading, or the manager may
      // not have a Cognito/user row yet (common on empty EU sandbox).
      let supervisors = supervisorUser?.userSub
        ? { [supervisorUser.userSub]: supervisorUser }
        : {};

      if (!!supervisorUser?.userSub) {
        const i = employees?.findIndex(
          (e) => e?.userSub === supervisorUser.userSub
        );
        if (i !== -1) {
          supervisors[supervisorUser.userSub] = {
            ...(supervisors[supervisorUser.userSub] || {}),
            ...(employees[i] || {}),
            ...(supervisorUser || {}),
          };
        }
      }

      const departmentRoles = (el?.departmentRoles || []).map((role) => {
        let employees = (employeesByDep[el.departmentId] || []).filter(
          (emp) => {
            // Supervisor is still matched by PRIMARY role only — a supervisor
            // should appear once at the top of the chart, not inside another
            // bucket they might also hold as a secondary role.
            if (emp?.employeeRoleName === "Supervisor") {
              supervisors[emp?.userSub] = {
                ...(supervisors[emp?.userSub] || {}),
                ...emp,
              };
              return false;
            }
            // Other roles: an employee appears under EVERY role they hold
            // (primary or secondary) so the org chart reflects real coverage.
            return employeeHasRole(emp, role?.name);
          }
        );
        const orderIds = employeesOrder?.[el?.departmentId]?.[role?.id];
        if (!!orderIds?.length) {
          employees = sortArrayByIds(
            structuredClone(employees),
            orderIds,
            "employeeId"
          );
        } else {
          employees.sort(
            (a, b) =>
              new Date(a.employeeHireDate) - new Date(b.employeeHireDate)
          );
        }
        return { ...role, employees };
      });

      return {
        ...el,
        departmentRoles,
        supervisors: Object.values(supervisors),
      };
    });
    return deps;
  }, [departments, employees, teams, employeesOrder, users?.allUsers?.Items]);

  const imageFetcher = (googleDriveFileId) => {
    driveRequest
      .getImageSrc(googleDriveFileId)
      .then((file) => {
        if (isValidImage(file.src)) {
          setFetchedSrc((prev) => ({
            ...prev,
            [googleDriveFileId]: file.src,
          }));
        }
      })
      .catch((error) => console.log("error: ", error));
  };

  useEffect(() => {
    if (!employees?.length || !accessToken) return;
    for (const employee of employees) {
      const { googleDriveFileId } = employee;
      if (googleDriveFileId && !fetchedSrc[googleDriveFileId]) {
        imageFetcher(googleDriveFileId);
      }
    }
  }, [employees, accessToken]);

  useEffect(() => {
    return () => {
      Object.values(fetchedSrc).forEach((src) => {
        if (src) {
          URL.revokeObjectURL(src);
        }
      });
    };
  }, []);

  const isMobileView = useMediaQuery({
    query: "(max-width: 450px)",
  });

  const filters = (
    <div className="organigramaFilters">
      <span className="filterLabel">
        Data sot:
        <span style={{ fontWeight: 400 }}>
          {dayjsTZ().format("DD/MM/YYYY")}
        </span>
      </span>
      <span className="divider" />
      <span className="filterLabel">
        Dega:
        <Select
          showSearch
          variant={false}
          suffixIcon={<CaretDownFilled />}
          placeholder={"Filtro Team"}
          value={activeFilter?.departmentRoles?.employees?.employeeTeam}
          style={{ width: 200 }}
          onChange={(e) =>
            changeFilter("departmentRoles.employees.employeeTeam", e)
          }
          classNames={{ popup: { root: isDarkMode ? "darkDropdown" : undefined } }}
        >
          {teamsOptions}
        </Select>
      </span>
      <span className="divider" />
      <span className="filterLabel">
        Departamenti:
        <Select
          showSearch
          variant={false}
          suffixIcon={<CaretDownFilled />}
          placeholder={"Filtro Departament"}
          value={activeFilter?.departmentName}
          style={{ width: 200 }}
          onChange={(e) => changeFilter("departmentName", e)}
          classNames={{ popup: { root: isDarkMode ? "darkDropdown" : undefined } }}
          allowClear
        >
          {(mergedDepartments || [])
            .filter((e) => e?.departmentId !== ADMIN_DEP_ID)
            .map((dep) => {
              return (
                <Option key={dep.departmentId} value={dep.departmentName}>
                  {dep.departmentName}
                </Option>
              );
            })}
        </Select>
      </span>
      {Object.keys(getObjectChanges(defaultFilters, activeFilter)).length ? (
        <MondayButton
          className="mondayButtonRed"
          Icon={<FilterIconButton />}
          onClick={() => setActiveFilter({})}
        />
      ) : null}
    </div>
  );

  //region RETURN
  return (
    <Modal
      open={open}
      goBack={
        <span className="goBack" onClick={() => setSiderOpen((prev) => !prev)}>
          <HamburgerIcon fill={isDarkMode ? "#fbfbfb" : "#1d3445"} />
        </span>
      }
      title={<span style={{ width: 200 }}>Organigrama</span>}
      className={`organigramaModal ${isDarkMode ? "dark" : ""}`}
      onCancel={() => setOpen(false)}
      headerExtrasLeft={!isMobileView ? filters : null}
      fullScreen={true}
    >
      <div className="organigramaContainer">
        {!!isMobileView ? filters : null}
        {siderOpen ? (
          <div className="statusFiltersList">
            {(employeeStatuses || []).map((el) => {
              const className = `statusFilterItem ${
                activeFilter?.departmentRoles?.employees?.employeeStatus ===
                el?.name
                  ? "selected"
                  : ""
              }`;
              return (
                <span
                  key={el?.id ?? el?.name}
                  className={className}
                  onClick={() => {
                    changeFilter(
                      "departmentRoles.employees.employeeStatus",
                      el?.name
                    );
                  }}
                >
                  <DashboardBricks fill={isDarkMode ? "#fbfbfb" : "#1d3445"} />
                  <span className="statusTitle">{el?.name}</span>
                </span>
              );
            })}
          </div>
        ) : null}
        <ReactFlowProvider>
          <FlowChart
            {...{
              setOpen,
              fetchedSrc,
              activeFilter,
              defaultFilters,
              mergedDepartments,
            }}
          />
        </ReactFlowProvider>
      </div>
    </Modal>
  );
};

export default Organigrama;

const isValidImage = (imgSrc) => {
  if (!imgSrc) return false;
  const image = new Image();
  image.src = imgSrc;
  return image.complete && image.naturalWidth > 0;
};
